/* eslint-disable complexity */
import React, {useEffect, useState} from 'react';

import {Button, TextInput} from '@gravity-ui/uikit';
import {
    PlaceholderContainer,
    PlaceholderContainerProps,
    useBooleanState,
} from '@yandex-data-ui/cloud-components';
import {ErrorLayoutType} from 'common/constants';
import i18n from 'i18n';
import {getAuthHref} from 'ui/utils/getAuthUrl';
import {isUserSignIn} from 'ui/utils/isUserSignIn';
import {sdk} from 'ui/utils/sdk';

import {cnError} from './Error.constants';

const {
    iframe,
    cloud,
    routes: {afterAuthPath},
    user,
} = window.YC;

export const useError = (error?: ErrorLayoutType): PlaceholderContainerProps => {
    const [loading, enableLoading] = useBooleanState(false);
    const [federationIdInput, , , toggleFederationIdInput] = useBooleanState(false);
    const [federationId, setFederationId] = useState<string>();
    const [currentError, setError] = useState(error);

    useEffect(() => {
        if (currentError === ErrorLayoutType.NotPermitted && iframe) {
            sdk.form.checkEnabledCookies().then((enabled) => {
                if (!enabled) {
                    setError(ErrorLayoutType.CookiesNotAvailable);
                }
            });
        }
    }, [currentError]);

    const openWindow = (url: string) => {
        const win = window.open(url, iframe ? '_blank' : '_self');

        if (iframe) {
            const timer = setInterval(() => {
                if (!win || win.closed) {
                    clearInterval(timer);
                    enableLoading();
                    window.location.reload();
                }
            }, 500);
        }
    };

    const onLoginSso = () => {
        const currentUrl = new URL(window.location.href);
        currentUrl.pathname = currentUrl.pathname + 'authorize/';
        currentUrl.searchParams.set('federationId', federationId ?? '');

        openWindow(currentUrl.href);
    };

    switch (currentError) {
        case ErrorLayoutType.NotFound:
            return {
                status: PlaceholderContainer.Status.NotFound,
                title: i18n('error-page', 'title-404'),
                description: i18n('error-page', 'description-404'),
            };
        case ErrorLayoutType.NotPublished:
            return {
                status: PlaceholderContainer.Status.Empty,
                title: i18n('error-page', 'title-422'),
                description: i18n('error-page', 'description-422'),
            };
        case ErrorLayoutType.AccessDenied:
            return {
                status: PlaceholderContainer.Status.AccessDenied,
                title: i18n('error-page', 'title-403'),
                description: i18n('error-page', 'description-403'),
            };
        case ErrorLayoutType.RoleError:
            return {
                status: PlaceholderContainer.Status.AccessDenied,
                title: i18n('error-page', 'title-403'),
                description: i18n(
                    'error-page',
                    cloud ? 'description-403-role-error-int' : 'description-403-role-error-int',
                ),
            };
        case ErrorLayoutType.CookiesNotAvailable:
            return {
                status: PlaceholderContainer.Status.AccessDenied,
                title: i18n('error-page', 'title-cookies-not-available'),
                description: i18n('error-page', 'description-cookies-not-available'),
                action: {
                    text: i18n('error-page', 'button-cookies-not-available'),
                    view: 'normal',
                    handler: () => {
                        const currentUrl = new URL(window.location.href);
                        currentUrl.searchParams.delete('iframe');
                        window.open(currentUrl.href, '_blank');
                    },
                },
            };
        case ErrorLayoutType.NotPermitted: {
            const userSignIn = isUserSignIn(user);

            if (userSignIn) {
                return {
                    status: PlaceholderContainer.Status.AccessDenied,
                    title: i18n('error-page', 'title-for-employees-only'),
                    description: (
                        <>
                            {i18n('error-page', 'description-for-employees-only')}
                            {federationIdInput && (
                                <div className={cnError('Federation')}>
                                    <TextInput
                                        value={federationId}
                                        onUpdate={setFederationId}
                                        className={cnError('FederationInput')}
                                        placeholder={i18n(
                                            'error-page',
                                            'placeholder-federation-id',
                                        )}
                                    />
                                    <div className={cnError('FederationButtons')}>
                                        <Button
                                            onClick={onLoginSso}
                                            view="action"
                                            loading={loading}
                                        >
                                            {i18n('error-page', 'button-login')}
                                        </Button>
                                        <Button
                                            onClick={toggleFederationIdInput}
                                            view="normal"
                                            loading={loading}
                                        >
                                            {i18n('error-page', 'button-cancel')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    ),
                    action: !federationIdInput
                        ? [
                              {
                                  text: i18n('error-page', 'button-change-login'),
                                  view: 'action',
                                  handler: () => {
                                      const currentUrl = new URL(window.location.href);
                                      currentUrl.pathname = currentUrl.pathname + 'authorize/';

                                      openWindow(
                                          getAuthHref({redirectUrl: currentUrl.href, change: true}),
                                      );
                                  },
                                  loading,
                              },
                              {
                                  text: i18n('error-page', 'button-login-sso'),
                                  view: 'normal',
                                  handler: toggleFederationIdInput,
                              },
                          ]
                        : [],
                };
            }

            return {
                status: PlaceholderContainer.Status.AccessDenied,
                title: i18n('error-page', 'title-need-login'),
                description: i18n('error-page', 'description-need-login'),
                action: {
                    text: i18n('error-page', 'button-login'),
                    view: 'normal',
                    handler: () => {
                        let retpath;
                        if (iframe) {
                            const currentUrl = new URL(window.location.href);
                            currentUrl.pathname = afterAuthPath;
                            retpath = currentUrl.href;
                        }

                        openWindow(getAuthHref({redirectUrl: retpath}));
                    },
                    loading,
                },
            };
        }
        case ErrorLayoutType.OnlyInIframe:
            return {
                status: PlaceholderContainer.Status.NoSearchResults,
                title: i18n('error-page', 'title-only-in-iframe'),
            };
        case ErrorLayoutType.AlreadyAnswered:
            return {
                status: PlaceholderContainer.Status.NoSearchResults,
                title: i18n('error-page', 'title-already-answered'),
                description: i18n('error-page', 'description-already-answered'),
            };
        default:
            return {
                status: PlaceholderContainer.Status.InternalError,
                title: i18n('error-page', 'title-500'),
                description: i18n('error-page', 'description-500'),
            };
    }
};
